import { FC, useEffect } from 'react'
import { Button, Box, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'

import PasswordField from 'components/PasswordField'
import { FieldLabel, submitButtonSx } from 'components/Helpdesk/FormPrimitives'
import { useUserPasswordReset } from 'services/token/token.hooks'
import theme from 'styles/theme'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: '#F9FAFB'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.r4r.greyLight
  }
} as const

const SetNewPassword: FC = () => {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const navigation = useNavigate()
  const { mutateAsync } = useUserPasswordReset()
  const { token } = useParams<{ token: string }>()

  const form = useFormik({
    initialValues: {
      password: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      password: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      )
    }),
    onSubmit: async ({ password }, helpers) => {
      helpers.setSubmitting(true)
      try {
        await mutateAsync({ password, token: token as string })
        enqueueSnackbar(
          formatMessage({
            id: 'setNewPassword.success',
            defaultMessage:
              'Hasło zostało zmienione, teraz możesz się zalogować używając nowego hasła.'
          }),
          {
            variant: 'success'
          }
        )
        navigation('/auth/sign-in')
      } catch (e: any) {
        helpers.setErrors(e.response.data)
        enqueueSnackbar(
          formatMessage({
            id: 'setNewPassword.error',
            defaultMessage:
              'Nie można nadać nowego hasła. Sprawdź poprawność link lub nowego hasła.'
          }),
          {
            variant: 'error'
          }
        )
      }
      helpers.setSubmitting(false)
    }
  })

  useEffect(() => {
    queryClient.invalidateQueries()
    // eslint-disable-next-line
  }, [])

  return (
    <Box component='form' onSubmit={form.handleSubmit} sx={{ textAlign: 'left' }}>
      <Typography
        sx={{
          fontSize: '22px',
          fontWeight: 700,
          color: theme.r4r.navy,
          textAlign: 'center',
          mb: '6px'
        }}>
        {formatMessage({ id: 'setNewPassword.title', defaultMessage: 'Nowe hasło' })}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: theme.r4r.grey,
          textAlign: 'center',
          mb: '8px'
        }}>
        {formatMessage({
          id: 'setNewPassword.subtitle',
          defaultMessage: 'Ustaw nowe hasło do swojego konta'
        })}
      </Typography>

      <FieldLabel>
        {formatMessage({ id: 'setNewPassword.form.password', defaultMessage: 'Hasło' })}
      </FieldLabel>
      <PasswordField
        name='password'
        value={form.values.password}
        onChange={form.handleChange}
        fullWidth
        sx={fieldSx}
        disabled={form.isSubmitting}
        error={!!form.errors.password}
        helperText={!!form.errors.password && form.errors.password}
      />

      <LoadingButton
        fullWidth
        type='submit'
        loading={form.isSubmitting}
        disabled={form.isSubmitting}
        sx={{ ...submitButtonSx, mt: '28px', mb: '12px' }}>
        {formatMessage({
          id: 'setNewPassword.form.submit',
          defaultMessage: 'Ustaw nowe hasło'
        })}
      </LoadingButton>
      <Button
        onClick={() => navigation('/auth/sign-in')}
        fullWidth
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '13px',
          color: theme.r4r.azure,
          ':hover': { backgroundColor: 'transparent', color: theme.r4r.navy }
        }}>
        {formatMessage({
          id: 'setNewPassword.form.signIn',
          defaultMessage: 'Wróć do logowania'
        })}
      </Button>
    </Box>
  )
}

export default SetNewPassword
